import { Button } from "components/Theme/button";
import { Form, Formik } from "formik";
import type { AssetPosition, AssetPositionConfigurationInput } from "kubb";
import type React from "react";
import { ErrorBanner } from "../../../components/Error/ErrorBanner";
import BlockSpinner from "../../../components/Spinners/BlockSpinner";
import { useUpdateAssetPosition } from "../../../hooks/updateEntity";
import DataProcessingConfigurationForm from "../assetPositions/DataProcessingConfigurationForm";
import SFTPCredentialsDisplay from "./SFTPCredentialsDisplay";
import Switch2CMI6110IntegrationForm from "./Switch2CMI6110IntegrationForm";

// TODO: Remove once kubb types are regenerated from backend OpenAPI spec
interface Switch2CMi6110Params {
  integration_type?: "switch2_cmi6110";
  api_key: string;
  user_id: string;
  site_id?: string | null;
  device_list_cron?: string;
  data_polling_cron?: string;
}

interface IntegrationConfigurationTabProps {
  assetPosition: AssetPosition;
  refresh?: () => void;
}

interface DataProcessingFormValues {
  autoCreateNewAssets: string;
  autoMergeLocations: string;
}

interface IntegrationFormValues {
  integration: Switch2CMi6110Params;
  dataProcessing: DataProcessingFormValues;
}

function assetPositionToFormValues(
  assetPosition: AssetPosition,
): IntegrationFormValues {
  const integrationConfig = assetPosition.configuration
    ?.integration as Switch2CMi6110Params;

  return {
    integration: {
      integration_type: "switch2_cmi6110",
      api_key: integrationConfig?.api_key || "",
      user_id: integrationConfig?.user_id || "",
      site_id: integrationConfig?.site_id || null,
      device_list_cron: integrationConfig?.device_list_cron || "0 * * * ? *",
      data_polling_cron:
        integrationConfig?.data_polling_cron || "*/15 * * * ? *",
    },
    dataProcessing: {
      autoCreateNewAssets:
        assetPosition.configuration?.data_processing?.auto_create_new_assets?.toString() ||
        "true",
      autoMergeLocations:
        assetPosition.configuration?.data_processing?.auto_merge_locations?.toString() ||
        "true",
    },
  };
}

function formValuesToConfiguration(
  values: IntegrationFormValues,
): AssetPositionConfigurationInput {
  return {
    integration: values.integration,
    data_processing: {
      auto_create_new_assets:
        values.dataProcessing?.autoCreateNewAssets !== "false",
      auto_merge_locations:
        values.dataProcessing?.autoMergeLocations !== "false",
    },
  };
}

export default function IntegrationConfigurationTab(
  props: IntegrationConfigurationTabProps,
): React.ReactElement {
  const { update, loading, error } = useUpdateAssetPosition(
    props.assetPosition,
  );

  const integrationType =
    props.assetPosition.configuration?.integration?.integration_type;

  // Handle SFTP-based integrations (sftp_receiver, guru_systems_csv)
  if (
    integrationType === "sftp_receiver" ||
    integrationType === "guru_systems_csv"
  ) {
    return (
      <SFTPCredentialsDisplay
        assetPosition={props.assetPosition}
        refresh={props.refresh}
      />
    );
  }

  // Only show configuration for switch2_cmi6110
  if (integrationType !== "switch2_cmi6110") {
    return (
      <div className="text-gray-500 py-4">
        Configuration editing is not available for this integration type.
      </div>
    );
  }

  return (
    <BlockSpinner loading={loading}>
      <Formik
        initialValues={assetPositionToFormValues(props.assetPosition)}
        onSubmit={(v, { setSubmitting }) => {
          setSubmitting(true);
          update({
            ...props.assetPosition,
            configuration: formValuesToConfiguration(v),
          }).then(() => {
            props.refresh?.();
            setSubmitting(false);
          });
        }}
        enableReinitialize={true}
      >
        {({ isSubmitting, isValid, dirty }) => (
          <Form>
            <Switch2CMI6110IntegrationForm namespace="integration" />
            <DataProcessingConfigurationForm namespace="dataProcessing" />
            <Button
              type="submit"
              color="brandLight"
              disabled={isSubmitting || !isValid || !dirty}
            >
              Update Configuration
            </Button>
            <ErrorBanner error={error} />
          </Form>
        )}
      </Formik>
    </BlockSpinner>
  );
}
